import React, { useEffect, useRef, useState } from "react";

import AsyncSelect from "react-select/async";
import GoogleMapReact from "google-map-react";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../Layouts/PageLayouts";
import { GOOGLE_API_KEY } from "../../utils/constants/key";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { patchDistributorLocation } from "../../store/reducers/auth";
import { toast } from "react-toastify";
const MAP_DEFAULT = { lat: 13.7367, lng: 100.5232 }; // Bangkok default

// Fetch autocomplete suggestions using the new AutocompleteSuggestion API
const fetchSuggestions = async (inputValue) => {
  if (!inputValue || inputValue.length < 2) return [];
  try {
    const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");
    const request = { input: inputValue };
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    return suggestions.map((s) => ({
      label: s.placePrediction.text.toString(),
      value: {
        place_id: s.placePrediction.placeId,
        description: s.placePrediction.text.toString(),
      },
    }));
  } catch (err) {
    console.error("Autocomplete error:", err);
    return [];
  }
};

export default function Location() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState("");
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectValue, setSelectValue] = useState(null);

  const { user } = useSelector((state) => state.auth);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapsJSRef = useRef(null);

  useEffect(() => {
    setLocation(null);
    setAddress("");
    if(!user?.address){
      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          const pos = { lat: coords.latitude, lng: coords.longitude };
          setLocation(pos);
          reverseGeocode(pos.lat, pos.lng);
        },
        () => {
          console.warn("Location access denied.");
        }
      );    
    }else{
      const initialPos = {
        lat: parseFloat(user.latitude),
        lng: parseFloat(user.longitude),
      };
      setLocation(initialPos);
      setAddress(user.address || "");
      //reverseGeocode(initialPos.lat, initialPos.lng);
    }
    
  }, [user]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}`
      );
      const data = await res.json();
      const result = data.results?.[0];
      if (result) setAddress(result.formatted_address);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMapLoaded = ({ map, maps }) => {
    try {
      mapRef.current = map;
      mapsJSRef.current = maps;

      const initialPos = location || MAP_DEFAULT;

      markerRef.current = new maps.Marker({
        position: initialPos,
        map,
        draggable: true,
        icon: {
          url: require("../../assets/images/map-marker.png"),
          scaledSize: new maps.Size(23, 28),
          anchor: new maps.Point(12, 28),
        },
      });

      maps.event.addListener(markerRef.current, "dragend", (e) => {
        const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setLocation(newPos);
        reverseGeocode(newPos.lat, newPos.lng);
      });

      // Set map loaded after everything is initialized
      setMapLoaded(true);
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  };

  const updateMapAndMarker = (lat, lng) => {
    const newPos = { lat, lng };
    setLocation(newPos);
    if (markerRef.current) markerRef.current.setPosition(newPos);
    if (mapRef.current) mapRef.current.panTo(newPos);
  };

  const handlePlaceSelect = async (place) => {
    if (!place?.value?.place_id) {
      console.warn("Invalid place selection");
      return;
    }
    setSelectValue(place);
    try {
      const { Place } = await window.google.maps.importLibrary("places");
      const placeObj = new Place({ id: place.value.place_id });
      await placeObj.fetchFields({ fields: ["formattedAddress", "location"] });
      const lat = placeObj.location.lat();
      const lng = placeObj.location.lng();
      setAddress(placeObj.formattedAddress || place.label);
      updateMapAndMarker(lat, lng);
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  const handleConfirm = async () => {
    if (!location || !address) return;

    if (user?.profile_data_id) {
      const res = await dispatch(patchDistributorLocation({
        id: user.profile_data_id,
        address,
        latitude: location.lat,
        longitude: location.lng,
      }));
      if (!res.error) {
        toast.success(t("profile_updated_success"));
      } else {
        toast.error(t("something_went_wrong"));
      }
    }

    navigate("/profile", {
      state: { address, location },
    });
  };

  return (
    <PageLayout headerTitle={t("location")} showBackBtn showSearchField={false}>
      <div
        className="location-header"
        style={{
          flexDirection: "row",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 80,
          marginTop: 20,
        }}
      >
        <div style={{ flex: 1, marginRight: 10 }}>
          <AsyncSelect
            value={selectValue || (address ? { label: address, value: address } : null)}
            loadOptions={fetchSuggestions}
            onChange={handlePlaceSelect}
            placeholder="Search for a location"
            isClearable
            getOptionValue={(opt) => opt.value?.place_id || opt.value}
          />
        </div>
        <div className="input-group button">
          <Button
            type="button"
            variant="standard"
            color="primary"
            onClick={handleConfirm}
          >
            {t("confirm_location")}
          </Button>
        </div>
      </div>

      <div style={{ height: "calc(100vh - 200px)", width: "100%" }}>
        
          <GoogleMapReact
            bootstrapURLKeys={{
              key: GOOGLE_API_KEY,
              libraries: ["places", "geometry"]
            }}
            defaultCenter={MAP_DEFAULT}
            center={location || MAP_DEFAULT}
            defaultZoom={14}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={handleMapLoaded}
          />
       
      </div>
    </PageLayout>
  );
}
